import {
  ConsultantCompetitorsSectionInput,
  ConsultantContactsSectionInput,
  ConsultantContactTypeSectionInput,
  ConsultantEducationSectionInput,
  ConsultantEmergencyContactsSectionInput,
  ConsultantEmployeeNumberSectionInput,
  ConsultantFormInput,
  ConsultantLanguageSkillsSectionInput,
  ConsultantPersonalSectionInput,
  ConsultantPrescriptionCodeSectionInput,
  ConsultantProfessionInput,
  ConsultantStatus,
  ConsultantWorkExperienceSectionInput,
  DoctorProfessionInput,
  FormAddressInput,
  GDPRInput,
  GenericProfessionInput,
  NurseProfessionInput,
  ProfessionInputType,
  PsykologProfessionInput,
  RadioNurseProfessionInput,
  SocionomProfessionInput,
  TeacherProfessionInput
} from "@bonliva-crm/graphql";
import { Professions } from "@bonliva-crm/shared";

/**
 * Legacy per-profession expertise data that some stores still read/write on the
 * form (e.g. `expertise.<index>.methods`). It is not part of the submit payload.
 */
export type ConsultantExpertiseFormEntry = {
  professionName?: string;
  systems?: string[];
  methods?: string[];
};

export type ConsultantFormData = {
  isNew: boolean;
  consultantId: string;
  expertise?: Record<string, ConsultantExpertiseFormEntry>;
  references?: { items?: unknown[] };
  responsiblePerson: { label: string; value: string } | null;
  professions: Array<
    | GenericProfessionInput
    | DoctorProfessionInput
    | NurseProfessionInput
    | TeacherProfessionInput
    | SocionomProfessionInput
    | PsykologProfessionInput
    | RadioNurseProfessionInput
  >;
  GDPRConsentRecords: GDPRInput[];
  address: FormAddressInput;
  competitors: ConsultantCompetitorsSectionInput;
  contactType: ConsultantContactTypeSectionInput;
  contacts: ConsultantContactsSectionInput;
  education: ConsultantEducationSectionInput;
  emergencyContacts: ConsultantEmergencyContactsSectionInput;
  employeeNumber: ConsultantEmployeeNumberSectionInput;
  extraCurriculum: string;
  knownSystems: string[];
  languageSkills: ConsultantLanguageSkillsSectionInput;
  personal: ConsultantPersonalSectionInput;
  prescriptionCode: ConsultantPrescriptionCodeSectionInput;
  status: ConsultantStatus;
  workExperience: ConsultantWorkExperienceSectionInput;
};

export const initialFormData: ConsultantFormData = {
  isNew: false,
  consultantId: null,
  responsiblePerson: null,
  professions: [],
  GDPRConsentRecords: [],
  address: {
    city: "",
    country: "",
    streetName: "",
    zipCode: ""
  },
  competitors: {
    items: []
  },
  contactType: {},
  contacts: {
    cellPhone: "",
    email: "",
    secondaryEmail: "",
    initialContact: null,
    selfRegisterOption: null,
    phone: "",
    preferredCommunication: [],
    recommendedByConsultant: null
  },
  education: {
    items: []
  },
  emergencyContacts: {
    items: []
  },
  employeeNumber: {
    employeeNumber: "",
    employeeNumber2: "",
    employeeNumber3: ""
  },
  extraCurriculum: "",
  knownSystems: [],
  languageSkills: {
    items: []
  },
  personal: {
    about: "",
    accommodationPreferences: "",
    care: "",
    commentAuthorization: "",
    countries: [],
    drivingLicenceType: null,
    firstComment: "",
    firstName: "",
    hasDrivingLicence: false,
    hprNumber: "",
    important: "",
    intro: "",
    isApprovedAuthorization: false,
    isWorkedNorway: false,
    lastName: "",
    norwayConsultantStatus: null,
    norwegianSsn: "",
    ssn: "",
    travelPreferences: ""
  },
  prescriptionCode: {
    code: ""
  },
  status: ConsultantStatus.Unverified,
  workExperience: {
    items: []
  }
};

export function prepareDataForSubmit(data: ConsultantFormData): ConsultantFormInput {
  const professions: ConsultantProfessionInput[] = [];

  for (const prof of data.professions) {
    switch (prof.professionName) {
      // ---------- GENERIC ----------
      case Professions.SE_ARBETSTERAPEUT:
      case Professions.SE_AUDIONOM:
      case Professions.SE_BARNMORSKA:
      case Professions.SE_BIOMEDICINSK_ANALYTIKER:
      case Professions.SE_DIETIST:
      case Professions.SE_FARMACEUTER:
      case Professions.SE_FYSIOTERAPEUT:
      case Professions.SE_HALSO_OCH_SJUKVARDKURATOR:
      case Professions.SE_INTERNREKRYTERING:
      case Professions.SE_MEDICINSKA_SEKRETERARE:
      case Professions.SE_PSYKOTERAPEUT:
      case Professions.SE_USK:
      case Professions.DK_JORDEMODER:
      case Professions.DK_SOCIALPAEDAGOG:
      case Professions.DK_HJAELPEPLEJER:
      case Professions.DK_FAMILJEBEHANDLARE:
      case Professions.DK_SOCIAL_OG_SUNDHEDSHJAELPER:
      case Professions.DK_PLEJER:
      case Professions.DK_SYGEHJAELPER:
      case Professions.DK_OMSORGSMEDARBEJDER:
      case Professions.DK_PSYKIATRIMEDARBEJDER:
      case Professions.DK_TERAPEUT:
      case Professions.DK_PAEDAGOGISK_ASSISTENT:
      case Professions.DK_SOCIALRAADGIVER:
      case Professions.DK_ERGOTERAPEUT:
      case Professions.DK_PAEDAGOGMEDHJAELPER:
        professions.push({
          type: ProfessionInputType.GENERIC,
          professionName: prof.professionName,
          genericProfession: prof as GenericProfessionInput
        });
        break;

      // ---------- DOCTOR ----------
      case Professions.SE_LAKARE:
      case Professions.NO_LEGE:
      case Professions.DK_LAEGER:
      case Professions.FI_LAAKARI:
        professions.push({
          type: ProfessionInputType.DOCTOR,
          professionName: prof.professionName,
          doctorProfession: prof as DoctorProfessionInput
        });
        break;

      // ---------- NURSE ----------
      case Professions.SE_SSK:
      case Professions.NO_SYKEPLEIE:
      case Professions.DK_SYGEPLEJERSKER:
      case Professions.FI_SAIRAANHOITAJA:
        professions.push({
          type: ProfessionInputType.NURSE,
          professionName: prof.professionName,
          nurseProfession: prof as NurseProfessionInput
        });
        break;

      // ---------- TEACHER ----------
      case Professions.SE_LARARE:
        professions.push({
          type: ProfessionInputType.TEACHER,
          professionName: prof.professionName,
          teacherProfession: prof as TeacherProfessionInput
        });
        break;

      // ---------- SOCIONOM ----------
      case Professions.SE_SOCIONOM:
        professions.push({
          type: ProfessionInputType.SOCIONOM,
          professionName: prof.professionName,
          socionomProfession: prof as SocionomProfessionInput
        });
        break;

      // ---------- PSYKOLOG ----------
      case Professions.SE_PSYKOLOG:
        professions.push({
          type: ProfessionInputType.PSYKOLOG,
          professionName: prof.professionName,
          psykologProfession: prof as PsykologProfessionInput
        });
        break;

      // ---------- RADIOGRAPHERS ----------
      case Professions.DK_SOCIAL_OG_SUNDHEDSASSISTENT:
      case Professions.DK_RADIOGRAFER:
      case Professions.SE_RONTGENSJUKSKOTERSKA:
        professions.push({
          type: ProfessionInputType.RADIOGRAPHERS,
          professionName: prof.professionName,
          radiographersProfession: prof as RadioNurseProfessionInput
        });
        break;

      // ---------- FALLBACK ----------
      default:
        throw new Error("Unknown profession");
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (data.contactType?.employeeContractType === "") {
    data.contactType.employeeContractType = null;
  }

  return {
    consultantId: data.consultantId,
    responsiblePersonId: data.responsiblePerson?.value,
    isNew: data.isNew,
    status: data.status,
    address: data.address,
    competitors: data.competitors,
    contacts: data.contacts,
    contactType: data.contactType,
    education: data.education,
    emergencyContacts: data.emergencyContacts,
    employeeNumber: data.employeeNumber,
    languageSkills: data.languageSkills,
    personal: data.personal,
    prescriptionCode: data.prescriptionCode,
    professions,
    workExperience: data.workExperience,
    knownSystems: data.knownSystems,
    extraCurriculum: data.extraCurriculum,
    GDPRConsentRecords: data.GDPRConsentRecords
  };
}
